<template>
  <div class="text-gray-200">
    <div as="h3" class="text-lg font-medium leading-6">Create New List</div>
    <form @submit.prevent="saveListDetails">
      <div class="mt-2">
        <label class="block text-sm font-medium text-gray-300">
          List Name
        </label>
        <input
          v-model="listName"
          type="text"
          class="block py-2 px-3 mt-1 w-full text-gray-200 bg-gray-900 rounded-md border border-gray-500 shadow-xs focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
      </div>

      <div class="mt-4">
        <label class="block text-sm font-medium text-gray-300">
          Description
        </label>
        <textarea
          v-model="listDescription"
          rows="6"
          maxlength="500"
          class="block py-2 px-3 mt-1 w-full text-gray-200 bg-gray-900 rounded-md border border-gray-500 shadow-xs focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"></textarea>
        <p class="text-xs text-gray-500">
          {{ listDescription.length }}/500 characters
        </p>
      </div>

      <!-- Replaced Listbox with RadioGroup for grid selection -->
      <!-- <div class="mt-4">
                <RadioGroup v-model="listIcon">
                  <RadioGroupLabel
                    class="block text-sm font-medium text-gray-900">
                    List Icon
                  </RadioGroupLabel>
                  <div class="grid grid-cols-10 gap-4 mt-2">
                    <RadioGroupOption
                      v-for="option in listIconOptions"
                      :key="option"
                      :value="option"
                      class="flex relative flex-col items-center py-2 px-4 rounded-lg border-2 cursor-pointer focus:outline-hidden"
                      :class="{
                        'border-indigo-600': listIcon === option,
                        'border-gray-300': listIcon !== option
                      }">
                      <div class="flex justify-center items-center p-1">
                        <fa-icon :icon="option" size="lg text-gray-300" />
                      </div>
                    </RadioGroupOption>
                  </div>
                </RadioGroup>
              </div> -->

      <!-- Error message -->
      <div
        v-if="editFormError"
        class="p-3 mt-4 text-sm rounded-lg cursor-pointer bg-red-500/20 border border-red-500/50 text-red-200"
        @click="editFormError = null">
        <div class="flex items-center gap-2">
          <fa-icon icon="exclamation-circle" />
          <span>{{ errorMessage }}</span>
        </div>
        <p class="text-xs text-red-300/70 mt-1">Tap to dismiss</p>
      </div>

      <!-- Action buttons footer -->
      <div class="pt-4 mt-4 border-t border-white/10">
        <div class="flex gap-2">
          <button
            type="button"
            class="flex-1 py-3 text-sm font-medium text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
            @click="closeModal()">
            Cancel
          </button>
          <button
            type="button"
            :disabled="isSaving"
            class="flex-[2] flex items-center justify-center gap-2 py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            @click="saveListDetails">
            <template v-if="isSaving">
              Creating...
              <fa-icon icon="spinner-third" class="fa-spin" />
            </template>
            <template v-else> Create List </template>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStoreLists } from '@/stores/storeLists'
import { useListIcons } from '@/use/useListIcons'
import { useHapticFeedback } from '@/composables/useHapticFeedback'

const router = useRouter()
const haptics = useHapticFeedback()

const props = defineProps({
  clubId: {
    type: String,
    default: null
  }
})
const emit = defineEmits(['close', 'list-created'])

const listName = ref('')
const listDescription = ref('')
const listBackdropPath = ref('')
const isSaving = ref(false)

const { iconOptions: listIconOptions } = useListIcons()
const listIcon = ref(listIconOptions[0]) // @todo - this will reset each time
const editFormError = ref(null)
const currentIndex = ref(0) // For tracking the current image index
const selectedImage = ref(null) // Selected image

const availableBackdropImages = computed(() => {
  const backdrops = []

  // Now ensure that we only have unique values, AND put the current value at the start
  if (listBackdropPath.value) {
    backdrops.unshift(listBackdropPath.value)
  }
  return [...new Set(backdrops)]
})

const totalImages = computed(() => {
  return availableBackdropImages.value.length || 0
})

const storeLists = useStoreLists()

// Error message computed property
const errorMessage = computed(() => {
  if (!editFormError.value) return ''
  if (typeof editFormError.value === 'string') return editFormError.value
  return (
    editFormError.value?.message || 'Failed to create list. Please try again.'
  )
})

const closeModal = () => {
  haptics.light()
  emit('close')
}

// Function to save the edited list details
async function saveListDetails() {
  if (isSaving.value) return

  haptics.medium()
  isSaving.value = true

  // Build list data with optional club_id
  const listData = {
    title: listName.value,
    description: listDescription.value,
    icon: listIcon.value
  }

  // Include club_id if creating a club list
  if (props.clubId) {
    listData.club_id = props.clubId
  }

  try {
    const res = await storeLists.addList(listData)
    const newList = res.data?.list

    haptics.success()
    emit('list-created', newList)
    closeModal()

    // Redirect to the newly created list
    if (newList?.slug) {
      router.push({
        name: 'WebUserListSingle',
        params: { listId: newList.slug }
      })
    } else if (newList?.id) {
      router.push({ name: 'WebUserListSingle', params: { listId: newList.id } })
    }
  } catch (error) {
    haptics.error()
    editFormError.value = error
    isSaving.value = false
  }
}

const prevImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
    selectImage()
  }
}

const nextImage = () => {
  if (currentIndex.value < totalImages.value - 1) {
    currentIndex.value++
    selectImage()
  }
}

const selectImage = () => {
  selectedImage.value = availableBackdropImages.value[currentIndex.value]
  listBackdropPath.value = selectedImage.value
}

const listBackgroupUrl = computed(() => {
  if (listBackdropPath.value) {
    return 'https://image.tmdb.org/t/p/w300' + listBackdropPath.value
  }
  return 'placeholder.png'
})

watch(
  totalImages,
  (newVal) => {
    if (totalImages.value > 0 && listBackdropPath.value === null) {
      selectImage()
    }
  },
  { immediate: true }
)
</script>
